import { Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { DeleteButtonIcon } from 'src/component/Buttons/DeleteButtonIcon';
import SweetAlert from 'sweetalert2';
import { useHttpClient } from '../../../../../../security/hooks/axiosProvider';
import Breadcrumb from '../../../../breadcrumb';
import styles from '../../../../styles/style-setting.module.scss';
import CreateProductivity from '../create-production/create-production';
import EditProductivity from '../edit-production/edit-production';
import ProductionColumns from './table-header/production-columns';

// number of productions per page
const NUMBER_OF_PRODUCTIONS_PER_PAGE = 15;

const ListProductivityPage = (props) => {
  const [Productions, setProductions] = useState([]);
  const httpClient = useHttpClient(true);
  const [pageNumber, setPageNumber] = useState(0);
  const activityPerPage = NUMBER_OF_PRODUCTIONS_PER_PAGE;
  const pagesVisited = pageNumber * activityPerPage;
  const keycloak = useKeycloak().keycloak;
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [filterBySubActivity, setFilterBySubActivity] = useState('');
  let data = [];
  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };
  const [subActivities, setSubActivities] = useState([]);
  const [spinner, setSpinner] = useState({ state: false, id: '' });
  const pageCount = Math.ceil(
    (search !== ''
      ? Productions.filter((production) => production.name.includes(search))
      : filterBySubActivity !== ''
        ? Productions.filter((production) => production.subActivity.name.includes(filterBySubActivity))
        : Productions
    ).length / activityPerPage
  );

  const getProductions = useCallback(async () => {
    httpClient.current?.get(`/api/production/all`).then((response) => {
      setProductions(response.data);
    });
  }, [httpClient]);

  const deleteProductions = async (id) => {
    await httpClient.current?.delete(`/api/production/${id}`);
    getProductions();
  };

  const handleCheckBox = async (event, production) => {
    setSpinner({ state: true, id: production.id });
    const activityValidation = {
      id: production.id,
      name: production.name,
      subActivity: production.subActivity,
      unitProduction: production.unitProduction,
      unitDimension: production.unitDimensions,
      valid: event.target.checked,
    };
    const response = httpClient.current?.put(`/api/production/update`, activityValidation);
    if ((await response).status === 200) {
      getProductions();
      setSpinner({ state: false, id: production.id });
    }
  };

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [cachedSubActivities, setCachedSubActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);

  const handleSelectActivity = (e) => {
    const activityId = +e.target.value || null;
    if (!activityId) {
      setSelectedActivity(null);
      setFilterBySubActivity('');
      return;
    }

    const filteredSubActivities = cachedSubActivities.filter(({ activity }) => activity.id === activityId);
    setSelectedActivity(activityId);
    setSubActivities(filteredSubActivities);
  };

  useEffect(() => {
    const getSubActivities = async () => {
      httpClient.current?.get(`/api/subactivity/all`).then((response) => {
        setSubActivities(response.data);
        setCachedSubActivities(response.data);

        const dataActivities = response.data.map((production) => production.activity);
        const filteredActivities = dataActivities.filter(
          (value, index, self) => index === self.findIndex((t) => t.name === value.name)
        );
        setFilteredActivities(filteredActivities);
      });
    };
    getSubActivities();
    getProductions();
    if (keycloak.authenticated) {
      keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER')
        ? setRole('super admin')
        : keycloak.hasResourceRole('ROLE_AGRISED_ADMIN')
          ? setRole('admin')
          : setRole('user');
    }
  }, [getProductions, httpClient, keycloak]);

  let restPagination = Productions.filter((production) => production.name.toUpperCase().includes(search.toUpperCase()));
  if (selectedActivity !== null) {
    restPagination = restPagination.filter(({ subActivity }) => subActivity.activity.id === selectedActivity);
  }
  if (filterBySubActivity !== '') {
    restPagination = restPagination.filter(({ subActivity }) => subActivity.name === filterBySubActivity);
  }
  const displayProductions = restPagination.slice(pagesVisited, pagesVisited + activityPerPage);

  displayProductions.map((production) => {
    let action = (
      <div>
        <span>
          <EditProductivity production={production} productions={getProductions} />
        </span>
        <span>
          <DeleteButtonIcon
            title={props.t('delete_production')}
            onClick={() =>
              SweetAlert.fire({
                title: props.t('you_delete'),
                text: props.t('not_recover'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                cancelButtonText: props.t('cancel'),
                reverseButtons: true,
              }).then((result) => {
                if (result.value) {
                  deleteProductions(production.id);
                  SweetAlert.fire(props.t('Deleted!'), props.t('isDeleted'), 'success');
                } else {
                  SweetAlert.fire(props.t('isNotDeleted'));
                }
              })
            }
            className={`fa fa-trash ${styles['trash']}`}
          />
        </span>
        <div></div>
      </div>
    );
    let status =
      spinner.state === true && spinner.id === production.id ? (
        <div className={`loader-box  ${styles['spinner']}`}>
          <div className="loader-2"></div>
        </div>
      ) : (
        <input
          type="checkbox"
          disabled={role === 'admin'}
          name="status"
          checked={production?.valid}
          onChange={(e) => handleCheckBox(e, production)}
        />
      );

    return data.push({
      id: production.id,
      name: production.name,
      unitDimension: production.unitDimension,
      unitProduction: production.unitProduction,
      status: status,
      action: action,
    });
  });

  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('production_url')} url={'settings/production'} />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader>
                  <div className="float-right">
                    <CreateProductivity productions={getProductions} />
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="row">
                    <div className="col">
                      <input
                        className="form-control mb-2"
                        onChange={(event) => {
                          setSearch(event.target.value);
                          changePage({ selected: 0 });
                        }}
                        value={search}
                        placeholder={props.t('search_unit')}
                      />
                    </div>
                    <div className="col">
                      <select
                        name="activity"
                        id="activity"
                        className="form-control mb-2"
                        onChange={handleSelectActivity}
                      >
                        <option value="">{props.t('select_activity')}</option>
                        {filteredActivities.map((activity) => (
                          <option key={activity.id} value={activity.id}>
                            {activity.name}
                          </option>
                        ))}
                      </select>
                      {selectedActivity && (
                        <select
                          name="activity"
                          id="subActivity_activity"
                          value={filterBySubActivity}
                          className="form-control mb-2"
                          onChange={(event) => {
                            setFilterBySubActivity(
                              event.target.value === 'Choose an sub-activity'
                                ? ''
                                : event.target.value === 'Choisissez une sous-activité'
                                  ? ''
                                  : event.target.value
                            );
                            changePage({ selected: 0 });
                          }}
                        >
                          <option>{props.t('choose_sub_activity')}</option>
                          {subActivities.map((subActivity) => (
                            <option key={subActivity.id} value={subActivity.name}>
                              {subActivity.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {data.length === 0 ? (
                    <Alert variant="outlined" severity="info">
                      {props.t('no_production')}
                    </Alert>
                  ) : (
                    <ProductionColumns data={data} />
                  )}
                  <br />
                  <div className="float-right">
                    {Productions.length > NUMBER_OF_PRODUCTIONS_PER_PAGE && (
                      <ReactPaginate
                        previousLabel="<"
                        nextLabel=">"
                        pageCount={pageCount}
                        onPageChange={changePage}
                        containerClassName={styles['paginationButton']}
                        previousLinkClassName={styles['previousBtn']}
                        nextLinkClassName={styles['nextBtn']}
                        disabledClassName={styles['paginationDisabled']}
                        activeClassName={styles['paginationActive']}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};
export default translate(ListProductivityPage);
